import React, { Dispatch, SetStateAction, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { PanelProps } from "../../../../../../../../../components/Generic/UITabMaterial";
import useYupValidation from "../../../../../../../../../hooks/useYupValidation";
import { Project } from "../../../../../../../../../models/project";
import { BaseFieldProps, ProjectDetailsFields, ProjectFields, ProjectTabsTabs } from "../../types";
import { ProjectDetails } from "../Details";
import { ProjectFiles } from "../Files";
import useStyles, { CustomUITab, ModalCustom } from "./styles";

interface Props {
  title?: string;
  projectToUpdate?: Project;
  hasError?: boolean;
  isOpened: boolean;
  isLoading: boolean;
  onCloseModal: () => void;
  onSubmit: (project: Project) => void;
  isView?: boolean;
  handleChangeProject: (key: keyof Project, value: any) => void;
  setProject: Dispatch<SetStateAction<Project | undefined>>;
}

const RoomModal = ({
  title,
  isOpened,
  projectToUpdate,
  hasError,
  isLoading,
  onCloseModal,
  onSubmit,
  isView,
  handleChangeProject,
  setProject,
}: Props) => {
  const { t: translate } = useTranslation();

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);
  const [selectedLeftPanel, setSelectedLeftPanel] = useState<number>(0);

  const disabled = isView || isLoading;
  const classes = useStyles();

  const getTitle = () => {
    if (isView) {
      return translate(`${translate("view")} "${projectToUpdate?.name}"`);
    }
    if (projectToUpdate?.id) {
      return translate(`${translate("edit")} "${projectToUpdate?.name}"`);
    }

    return translate("newProject");
  };

  const schema = yup.object().shape({
    name: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    startDate: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    endDate: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    email: yup
      .string()
      .email(translate("pleaseEnterAValidEmail"))
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    emailHost: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    emailPort: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    emailUser: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    emailPassword: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
  });

  const clearFields = () => {
    setProject({} as Project);
  };

  const { fieldHasError, getErrorByField, someFieldHasError } = useYupValidation(errors);

  const generateBaseFieldProps = (name: ProjectFields) => {
    const fieldProps: BaseFieldProps = {
      error: fieldHasError(name),
      errorMessage: getErrorByField(name),
      "data-cy": `input-${name}`,
      dataCy: name,
      label: translate(name),
      placeholder: translate(name),
      "aria-label": name,
      disabled,
      name,
      value: projectToUpdate?.[name],
    };

    return fieldProps;
  };

  const handleValidateSuccess = (data?: Project): boolean => {
    schema.validateSync(data, { abortEarly: false });
    setErrors([]);

    return true;
  };

  const handleValidateError = (error: any): boolean => {
    if (error instanceof yup.ValidationError) setErrors(error?.inner);

    return false;
  };

  const isValid = (data?: Project) => {
    try {
      return handleValidateSuccess(data);
    } catch (error) {
      return handleValidateError(error);
    }
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    const data = projectToUpdate;

    if (isValid(data) && data) {
      onSubmit(data);
    }
  };

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      clearFields();
      onCloseModal();
    }
  }, [hasError]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      isValid(projectToUpdate);
    }
  }, [
    hasFormBeenSubmitted,
    ...Object.keys(projectToUpdate ?? {}).map(
      (key) => projectToUpdate?.[key as keyof typeof projectToUpdate]
    ),
  ]);

  const panels: PanelProps[] = [
    {
      menuItem: translate(ProjectTabsTabs.DETAILS),
      className: classes.modalTabBody,
      hasError: someFieldHasError(Object.values(ProjectDetailsFields)),
      render: () => (
        <ProjectDetails
          generateBaseFieldProps={generateBaseFieldProps}
          handleChangeProject={handleChangeProject}
          project={projectToUpdate}
        />
      ),
    },
    {
      menuItem: translate(ProjectTabsTabs.FILES),
      className: classes.modalTabBody,
      hasError: someFieldHasError(Object.values(ProjectDetailsFields)),
      render: () => <ProjectFiles isView={isView ?? false} projectId={projectToUpdate?.id ?? 0} />,
    },
  ];

  return (
    <ModalCustom
      data-cy="manageProjectodal"
      open={isOpened}
      handleClose={onCloseModal}
      title={title || getTitle()}
      dataCyTitle="manageProjectModalTitle"
      showActionButtons
      actionButtonProps={{
        onCancel: () => {
          clearFields();
          onCloseModal();
        },
        dataCySave: "submitButton",
        dataCyCancel: "cancelButton",
        textSave: projectToUpdate?.id ? translate("save") : translate("add"),
        loading: isLoading,
        onSave: disabled ? undefined : handleSubmit,
        saveDisabled: isLoading,
        cancelDisabled: isLoading,
      }}
      labelStatus={translate("status")}
    >
      <>
        {projectToUpdate?.id ? (
          <CustomUITab
            leftPanels={panels}
            isRenderingStatusField={true}
            selectedLeftPanel={selectedLeftPanel}
            onChangeLeftPanel={(_, value) => setSelectedLeftPanel(Number(value))}
            gridClassName={classes.modalTabGrid}
          />
        ) : (
          <ProjectDetails
            generateBaseFieldProps={generateBaseFieldProps}
            handleChangeProject={handleChangeProject}
            project={projectToUpdate}
          />
        )}
      </>
    </ModalCustom>
  );
};

export default RoomModal;
